import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { FiEdit2 } from 'react-icons/fi';
import PageForm from '../../templates/PageForm';
import ATable from '../../organisms/ATable';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import {
    COLLATERAL_BREADCRUMBS,
    COLLATERAL_ROUTES,
    formatCurrency,
    formatDate,
    getGuaranteeStatusColor,
    getIntensityColor,
} from './collateralUtils';

const DocBadge = ({ value }: { value: string }) => {
    if (!value || value === 'N/A') return <span className="text-gray-400 text-xs">—</span>;
    const colorMap: Record<string, string> = {
        'TEM': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        'NÃO TEM': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
        'QUITADA': 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
    };
    return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colorMap[value] || 'bg-gray-100 text-gray-600'}`}>{value}</span>;
};

const GuaranteePageView = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        if (!id) return;
        setIsLoading(true);
        collateralApi.getGuaranteeView(id)
            .then(setData)
            .catch((e: any) => {
                if (e instanceof ApiError) {
                    if (e.status === 403) return navigate('/denied');
                    if (e.status === 404) return navigate('/not-found');
                }
                navigate('/error');
            })
            .finally(() => setIsLoading(false));
    }, [id]);

    const titles = data?.titles || [];

    const summary = useMemo(() => {
        const totalAmount = titles.reduce((s: number, t: any) => s + t.amount, 0);
        const docsCompleteAmount = titles.filter((t: any) => t.docStatus === 'OK').reduce((s: number, t: any) => s + t.amount, 0);
        return { totalAmount, docsCompleteAmount, docsIncompleteAmount: totalAmount - docsCompleteAmount };
    }, [data]);

    if (isLoading) return <div className="p-8 text-gray-500">Carregando...</div>;
    if (!data) return <div className="p-8 text-red-600">Garantia não encontrada</div>;

    const pct = (v: number) => summary.totalAmount > 0 ? ((v / summary.totalAmount) * 100).toFixed(1) + '%' : '0%';

    return (
        <>
        <PageForm
            pageTitle={`${data.operationCode} — ${data.assignor}`}
            apiVersion={data.apiVersion ? `${data.apiName} - ${data.apiVersion} (${(data.environment || '').toUpperCase()})` : ''}
            isLoading={false}
            breadcrumb={[
                COLLATERAL_BREADCRUMBS.structuring,
                COLLATERAL_BREADCRUMBS.guarantees,
                { label: data.operationCode, url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end space-x-2">
                    <button onClick={() => navigate(COLLATERAL_ROUTES.guarantees)}
                        className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded">
                        <IoMdArrowRoundBack />
                    </button>
                    <button onClick={() => navigate(`/collateral/guarantees/${id}/view/edit`)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-sm flex items-center space-x-2">
                        <FiEdit2 size={14} /> <span>Editar</span>
                    </button>
                </div>
            }
        >
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-4 my-4">
                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Configuração</h3>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-500">Status</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getGuaranteeStatusColor(data.guaranteeStatus)}`}>{data.guaranteeStatus}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Intensidade</span>
                            {data.analysisIntensity ? <span className={`px-2 py-0.5 rounded text-xs font-medium ${getIntensityColor(data.analysisIntensity)}`}>{data.analysisIntensity}</span> : <span className="text-gray-400">—</span>}</div>
                        <div className="flex justify-between"><span className="text-gray-500">Critério</span><span className="dark:text-white">{data.analysisCriteria || '—'}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Trader</span><span className="dark:text-white">{data.trader || '—'}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Títulos</span><span className="dark:text-white">{data.totalTitles}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Valor Total</span><span className="font-medium dark:text-white">{formatCurrency(data.totalAmount)}</span></div>
                    </div>
                </div>

                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Checagem dos Documentos</h3>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-500">Docs completo R$</span><span className="text-green-600 font-medium">{formatCurrency(summary.docsCompleteAmount)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">% completo</span><span className="dark:text-white">{pct(summary.docsCompleteAmount)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Incompleto R$</span><span className="text-red-600 font-medium">{formatCurrency(summary.docsIncompleteAmount)}</span></div>
                    </div>
                </div>

                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Validação</h3>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-500">% validados</span><span className="dark:text-white">{data.validatedPercentage}%</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="dark:text-white">{data.validationStatus}</span></div>
                    </div>
                </div>
            </div>

            {/* Titles table */}
            <div className="mb-4">
                <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden" style={{ height: 'calc(100vh - 380px)' }}>
                    <ATable autoResize marginTop={0} headers={[
                        { id: 'titleNumber', title: 'Título', field: 'titleNumber', canSort: true, width: '70px' },
                        { id: 'debtorName', title: 'Sacado', field: 'debtorName', canSort: true },
                        { id: 'debtorDocument', title: 'CNPJ/CPF', field: 'debtorDocument', canSort: false, width: '150px' },
                        { id: 'city', title: 'Município', field: 'city', canSort: true, width: '130px' },
                        { id: 'state', title: 'UF', field: 'state', canSort: true, width: '40px', cssClass: 'text-center' },
                        { id: 'dueDate', title: 'Venc.', field: 'dueDate', canSort: true, width: '90px' },
                        { id: 'amount', title: 'Valor', field: 'amount', canSort: true, width: '110px', cssClass: 'text-right' },
                        { id: 'docStatus', title: 'Status', field: 'docStatus', canSort: true, width: '80px', cssClass: 'text-center' },
                    ]}>
                        {titles.map((row: any, index: number) => (
                            <tr key={row.id} className={`hover:bg-blue-50 dark:hover:bg-slate-700 ${index % 2 === 0 ? 'bg-gray-50 dark:bg-slate-800' : 'dark:bg-slate-900'}`}>
                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.titleNumber}</td>
                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white truncate max-w-[200px]" title={row.debtorName}>{row.debtorName}</td>
                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white whitespace-nowrap">{row.debtorDocument}</td>
                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.city}</td>
                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white text-center">{row.state}</td>
                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white whitespace-nowrap">{formatDate(row.dueDate)}</td>
                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white text-right whitespace-nowrap">{formatCurrency(row.amount)}</td>
                                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center"><DocBadge value={row.docStatus} /></td>
                            </tr>
                        ))}
                    </ATable>
                </div>
            </div>
        </PageForm>
            <Outlet />
        </>
    );
};

export default GuaranteePageView;
